import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconClock } from "@/assets/icons/geist/IconClock";
import { IconError } from "@/assets/icons/geist/IconError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import type { BaseIconProps } from "@/assets/icons/utils";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Pill, type PillProps } from "@/components/podkit/status/Pill";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { AgentExecutionState, agentExecutionState } from "@/utils/agent-executions";
import { useMemo, type FC } from "react";

type AgentExecutionStatusPillProps = {
    agentExecution: PlainAgentExecution;
} & PropsWithClassName;

export const AgentExecutionStatusPill: FC<AgentExecutionStatusPillProps> = ({ agentExecution, className }) => {
    const pillConfig = useMemo<{
        label: string | null;
        variant: PillProps["variant"];
        LeadingIcon?: React.ComponentType<BaseIconProps>;
        className?: string;
        tooltip?: string;
    }>(() => {
        const state = agentExecutionState(agentExecution);
        if (!state) {
            return { label: null, variant: "neutral" };
        }

        switch (state) {
            case AgentExecutionState.WORKING:
                return { label: state, variant: "neutral", LeadingIcon: IconSpinner };
            case AgentExecutionState.WAITING_FOR_INPUT:
                return { label: state, variant: "warning", LeadingIcon: IconClock };
            case AgentExecutionState.FAILED:
                return { label: state, variant: "danger", LeadingIcon: IconError };
            case AgentExecutionState.READY_FOR_REVIEW:
                return { label: state, variant: "success", LeadingIcon: IconCheckFill };
            case AgentExecutionState.STOPPED_BY_USER:
                return { label: state, variant: "neutral" };
            case AgentExecutionState.STOPPING:
                return { label: state, variant: "neutral", LeadingIcon: IconSpinner };
            default:
                return { label: null, variant: "neutral" };
        }
    }, [agentExecution]);

    if (!pillConfig.label) {
        return null;
    }

    return (
        <Pill
            variant={pillConfig.variant}
            size="sm"
            LeadingIcon={pillConfig.LeadingIcon}
            tooltip={pillConfig.tooltip}
            className={cn("shrink-0 whitespace-nowrap", pillConfig.className, className)}
        >
            {pillConfig.label}
        </Pill>
    );
};
